import React, { PureComponent } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import Radio from '@active/react-ui/Radio';
import RadioGroup from '@active/react-ui/RadioGroup';
import Form, { FormConsumer } from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormLabel from '@active/react-ui/FormLabel';
import FormField from '@active/react-ui/FormField';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import Locale from '@active/react-utils/i18n/Locale';

import { savedCartType } from '../types';
import Images from '../imgs/ccImgs/imgMap';
import encrypt from '../utils/encrypt';
import getCardType from '../utils/getCardType';
import { enableCVV, executeCaptcha } from '../utils/common';
import CVVField from '../components/CVVField';
import './SavedCreditCard.scss';

export class SavedCreditCard extends PureComponent {
  static propTypes = savedCartType

  static defaultProps = {
    savedAccounts: null,
    // isAbleToSavePrimary: true
    defaultAccount: '',
    creditCardSwitch: null,
    handleSwitch: noop,
    fields: {},
    amsConfig: {},
    displayCVVHelpTextAfterLabel: false,
    hideCVVHelpText: false,
  }

  constructor(props) {
    super(props);
    const {
      fields, savedAccounts,
      defaultAccount, triggers: { commitTrigger, validateTrigger = noop },
    } = props;
    if (savedAccounts && savedAccounts.length) {
      const firstUsableAccountIndex = savedAccounts.findIndex(({ isExpired }) => !isExpired);
      this.state = {
        selectedAccountIndex: defaultAccount || (firstUsableAccountIndex > -1 ? `${firstUsableAccountIndex}` : ''),
        fields,
      };
      commitTrigger(this.committer);
      validateTrigger((callback) => {
        this.form.validateForm({ shouldTouchFields: true }).then(
          (res) => {
            callback(isEmpty(res));
          },
        );
      });
    }
  }

  committer = (somethingNeverUsed, committerCallback) => {
    this.setState({ committerCallback }, () => {
      this.form.validateForm({ shouldTouchFields: true }).then(
        (res) => {
          if (isEmpty(res)) {
            this.form.submitForm();
          } else {
            committerCallback({ errorCode: 'INVALID_PARAMS' });
          }
        },
      );
    });
  }

  handleSaveAmsAccount = ({ params, successCallbackParams }) => {
    const {
      services: { choseSavedAccount },
    } = this.props;
    const { committerCallback } = this.state;
    choseSavedAccount({
      params,
      resolve: (resp) => {
        if (resp === 'SUCCESS') {
          committerCallback(successCallbackParams);
        } else if (resp === 'InvalidCaptcha') {
          committerCallback({ errorCode: 'INVALID_CAPTCHA' });
        } else {
          committerCallback({ errorCode: 'ERROR_SAVE_CARD_FAILED' });
        }
      },
      reject: () => {
        committerCallback({ errorCode: 'ERROR_SAVE_CARD_FAILED' });
      },
    });
  }

  handleSubmit = ({ cvv }) => {
    const {
      amsConfig: {
        sessionId, modulus, exponent, enableCaptcha,
      },
      savedAccounts,
    } = this.props;
    const { committerCallback, selectedAccountIndex } = this.state;
    const cipherText = encrypt({ cvv, modulus, exponent });
    if (!savedAccounts[selectedAccountIndex]) {
      committerCallback({ errorCode: 'INVALID_PARAMS' });
      return;
    }

    const successCallbackParams = { sessionId };
    const params = {
      SessionID: sessionId,
      cipherText,
      modulus,
      EncryptedAccountId: savedAccounts[selectedAccountIndex].accountId,
    };
    if (enableCaptcha) {
      executeCaptcha(this.handleSaveAmsAccount, params, successCallbackParams, committerCallback);
    } else {
      this.handleSaveAmsAccount({ params, successCallbackParams });
    }
  }

  handleChange = ({ target: { value: selectedAccountIndex } }) => {
    const { handleSwitch } = this.props;
    this.form.setFieldValue('cvv', '');
    this.form.setFieldTouched('cvv', false);
    this.setState({ selectedAccountIndex });
    handleSwitch(selectedAccountIndex);
  }

  render() {
    const {
      savedAccounts, isCVVAvailable,
      collectCVVConfigs, creditCardSwitch,
      i18n, displayCVVHelpTextAfterLabel, hideCVVHelpText,
    } = this.props;
    if (!savedAccounts || !savedAccounts.length) {
      return null;
    }
    const { selectedAccountIndex, fields } = this.state;
    return (
      <div className="checkout__saved-cc checkout-cc">
        <Form
          onSubmit={this.handleSubmit}
          validateOnChange={false}
          initialValues={fields}>
          <div className="checkout__saved-cc__notice" id="form_group_label__accountId">
            <Locale id="checkout.lib.components.creditCard.savedCard.notice" />
          </div>
          <FormGroup name="accountId" withoutLayout>
            <FormLabel/>
            <FormField
              as={RadioGroup}
              role="radiogroup"
              aria-labelledby="form_group_label__accountId"
              value={selectedAccountIndex}
              onChange={this.handleChange}>
              { savedAccounts.map(({
                cardType, lastFourDigits, expiryMonth, expiryYear, isExpired,
              }, index) => {
                const isCVVEnable = enableCVV(cardType || 'INVALID', collectCVVConfigs, isCVVAvailable);

                return (<div key={index} className={`${isExpired ? 'row-disable ' : ''}checkout__saved-cc__item`}>
                  <div className='row'>
                    <Radio
                      role="radio"
                      className="checkout__saved-cc__radio"
                      disabled={isExpired}
                      value={`${index}`}
                      aria-disabled={isExpired}>
                      <div className={`${Array.isArray(Images[cardType]) ? 'two-img ' : ''}checkout__saved-cc__img`}>
                        {
                          (Array.isArray(Images[cardType]) ? Images[cardType] : [ Images[cardType] ]).map(
                            (image, i) => {
                              const imgAlt = i18n(
                                  `checkout.lib.components.payment.credit.cart.type.${getCardType(
                                    cardType,
                                    image)}`,
                              );
                              return <img
                                key={i}
                                src={image || Images.DEFAULT}
                                alt={imgAlt}/>;
                            },
                          )
                        }
                      </div>
                      <div
                        role="region"
                        className="checkout__saved-cc__digits"
                        aria-label={`
                        ${i18n('checkout.lib.components.payment.credit.cart.wcag.last4digits')}
                        ${lastFourDigits}
                        ${i18n('checkout.lib.components.payment.credit.cart.wcag.expiration.date',
                          { expiryMonth, expiryYear })}
                        `}>
                        <span>
                          ****
                        </span>
                        <span className="checkout__saved-cc__digits-number">{lastFourDigits}</span>
                      </div>
                      <div className="checkout__saved-cc__expiry">
                        <div aria-hidden className={`${isExpired ? 'expired ' : ''}checkout__saved-cc__expiry-text`}>
                          {expiryMonth && expiryYear ? `${expiryMonth}/${expiryYear}` : ''}
                        </div>
                      </div>
                    </Radio>
                  </div>
                  {
                    `${index}` === `${selectedAccountIndex}`
                    && <CVVField
                      withoutLayout
                      i18n={i18n}
                      hideCVVHelpText={hideCVVHelpText}
                      displayCVVHelpTextAfterLabel={displayCVVHelpTextAfterLabel}
                      isCVVEnable={isCVVEnable}/>
                  }
                </div>);
              },
              )}
              {creditCardSwitch}
            </FormField>
          </FormGroup>
          <FormConsumer>
            {
              ({
                form,
              }) => {
                this.form = form;
              }
            }
          </FormConsumer>
        </Form>
      </div>
    );
  }
}

export default withI18n(SavedCreditCard);
